<template>
    <div id="employee-absence-page">
        <ui5-button
            @click="$router.push('/employeedetails/' + idParam)"
            style="width: 200px; display: block"
        >
            {{ trans('pages.employee_absence.back') }}
        </ui5-button>
        <div class="leftPane">
            <h2>{{ trans('pages.employee_absence.absences') }}</h2>
            <table class="leave-list">
                <thead>
                    <tr>
                        <th class="dateHeading">
                            {{ trans('pages.employee_absence.date') }}
                        </th>
                        <th class="reasonHeading">
                            {{ trans('pages.employee_absence.reason') }}
                        </th>
                        <th class="actionHeading">
                            {{ trans('pages.employee_absence.actions') }}
                        </th>
                    </tr>
                </thead>
                <tbody v-if="absences">
                    <tr v-if="absences.length == 0">
                        <td colspan="3">
                            {{
                                trans('pages.employee_absence.no_absences', {
                                    year: selectedYear,
                                })
                            }}
                        </td>
                    </tr>
                    <template v-else>
                        <tr
                            v-for="(absence, absenceIndex) in absences"
                            :class="{ highlight: highlightedId == absence.id }"
                            :key="'leave.' + absence.id"
                        >
                            <td class="date">{{ absence.day }}</td>
                            <td class="reason">{{ absence.reason }}</td>
                            <td class="action">
                                <i
                                    class="fa fa-pencil"
                                    @click="edit(absence)"
                                    :alt="trans('pages.employee_absence.edit')"
                                    :title="
                                        trans('pages.employee_absence.edit')
                                    "
                                />
                                <i
                                    class="fa fa-trash"
                                    @click="
                                        deleteAbsence(absence.id, absenceIndex)
                                    "
                                    :alt="
                                        trans('pages.employee_absence.delete')
                                    "
                                    :title="
                                        trans('pages.employee_absence.delete')
                                    "
                                />
                            </td>
                        </tr>
                    </template>
                </tbody>
            </table>
            <div class="controls">
                <ui5-button @click="shiftDateForAbsenceList(-1)">
                    {{ trans('pages.employee_absence.previous') }}
                </ui5-button>
                <span>
                    {{
                        trans('pages.employee_absence.absences_for', {
                            year: selectedYear,
                        })
                    }}
                </span>
                <ui5-button @click="shiftDateForAbsenceList(+1)">
                    {{ trans('pages.employee_absence.next') }}
                </ui5-button>
            </div>
            <div id="addAbsenceEmployee">
                <h3>{{ trans('pages.employee_absence.add_absence') }}</h3>
                <div class="field">
                    <ui5-label
                        for="date"
                        required
                    >
                        {{ trans('pages.employee_absence.date_from') }}
                    </ui5-label>
                    <ui5-date-picker
                        :required="true"
                        ref="addDate"
                        id="date"
                        name="date"
                        :format-pattern="datePattern"
                        @click="$event.target.openPicker()"
                        @change="addAbsenceData.date = $event.detail.value"
                        :value="addAbsenceData.date"
                    />
                </div>
                <div class="field">
                    <ui5-label for="dateEnd">
                        {{ trans('pages.employee_absence.date_to') }}
                    </ui5-label>
                    <ui5-date-picker
                        id="dateEnd"
                        name="dateEnd"
                        ref="addDateEnd"
                        :format-pattern="datePattern"
                        @click="$event.target.openPicker()"
                        @change="addAbsenceData.dateEnd = $event.detail.value"
                        :value="addAbsenceData.dateEnd"
                    />
                </div>
                <div class="field">
                    <ui5-label for="reason">
                        {{ trans('pages.employee_absence.reason') }}
                    </ui5-label>
                    <ui5-input
                        class="reason"
                        type="Text"
                        id="reason"
                        name="reason"
                        @input="addAbsenceData.reason = $event.target.value"
                        :value="addAbsenceData.reason"
                    />
                    <div class="inputErrorMsg">
                        {{ trans('pages.employee_absence.enter_valid_reason') }}
                    </div>
                </div>
                <div class="field button-field">
                    <ui5-button
                        name="addAbsenceEmployeeButton"
                        @click="addAbsence"
                    >
                        {{ trans('pages.employee_absence.add') }}
                    </ui5-button>
                </div>
            </div>

            <div id="addForgottenDayEmployee">
                <h3>{{ trans('pages.employee_absence.add_forgotten_day') }}</h3>
                <div class="field">
                    <ui5-label
                        for="forgotten-date"
                        required
                    >
                        {{ trans('pages.employee_absence.date') }}
                    </ui5-label>
                    <ui5-date-picker
                        :required="true"
                        ref="forgottenDate"
                        id="forgotten-date"
                        name="date"
                        :format-pattern="datePattern"
                        @click="$event.target.openPicker()"
                        @change="forgottenDate = $event.detail.value"
                        :value="forgottenDate"
                    />
                </div>
                <div class="field button-field">
                    <ui5-button
                        name="addForgottenDayEmployeeButton"
                        :disabled="addingForgotten"
                        @click="addForgottenDay"
                    >
                        <div>
                            <spinner v-if="addingForgotten" />
                            {{ trans('pages.employee_absence.add') }}
                        </div>
                    </ui5-button>
                </div>
            </div>
        </div>
        <div class="rightPane">
            <div
                id="updateAbsenceForm"
                v-if="editing"
            >
                <h3>{{ trans('pages.employee_absence.edit_absence') }}</h3>
                <div class="field">
                    <ui5-label
                        for="edit-date"
                        required
                    >
                        {{ trans('pages.employee_absence.date') }}
                    </ui5-label>
                    <ui5-date-picker
                        :required="true"
                        ref="editDate"
                        id="edit-date"
                        name="date"
                        :format-pattern="datePattern"
                        @click="$event.target.openPicker()"
                        @change="editAbsenceData.day = $event.detail.value"
                        :value="editAbsenceData.day"
                    />
                </div>
                <div class="field">
                    <ui5-label for="reason">
                        {{ trans('pages.employee_absence.reason') }}
                    </ui5-label>
                    <ui5-input
                        class="reason"
                        type="Text"
                        name="reason"
                        :value="editAbsenceData.reason"
                        @input="editAbsenceData.reason = $event.target.value"
                    />
                </div>
                <div class="field button-field">
                    <ui5-button @click="updateAbsence">
                        {{ trans('pages.employee_absence.save') }}
                    </ui5-button>
                    <ui5-button @click="cancelUpdateAbsence">
                        {{ trans('pages.employee_absence.cancel') }}
                    </ui5-button>
                </div>
            </div>
        </div>
    </div>
</template>
<script>
import employeeApi from '../api/employee';
import userApi from '../api/user';
import Spinner from '../components/loader/Spinner';
import IdParamMixin from '../mixins/id-param';
import localization from '../mixins/localization';
import {
    showConfirmDialog,
    showInfoMessage,
    showErrorMessage,
} from '../dialogs';

export default {
    components: { Spinner },
    mixins: [IdParamMixin(), localization],
    computed: {
        datePattern: () => 'yyyy-MM-dd',
    },
    data() {
        return {
            addingForgotten: false,
            highlightedId: null,
            editing: false,
            selectedYear: null,
            absences: null,
            editAbsenceData: {
                day: '',
                reason: '',
                id: null,
            },
            addAbsenceData: {
                date: '',
                dateEnd: '',
                reason: '',
            },
            forgottenDate: '',
        };
    },
    methods: {
        load() {
            userApi.fetchUserDetails(this.idParam).then((response) => {
                const employee = response.data.data;
                this.selectedYear = parseInt(new Date().getFullYear());
                this.$store.commit(
                    'general/setPageTitle',
                    this.trans('pages.employee_absence.absences_for_employee', {
                        name: employee.name,
                        year: this.selectedYear,
                    })
                );
                this.getAbsences();
            });
        },
        cancelUpdateAbsence() {
            this.editing = false;
        },
        shiftDateForAbsenceList(direction) {
            this.selectedYear += direction;
            this.getAbsences();
        },
        getAbsences() {
            employeeApi
                .fetchAbsenceForYear(this.idParam, this.selectedYear)
                .then((response) => {
                    this.absences = response.data.data;
                });
        },
        deleteAbsence(id, index) {
            showConfirmDialog({
                title: this.trans(
                    'pages.employee_absence.confirm_delete_title'
                ),
                content: this.trans(
                    'pages.employee_absence.confirm_delete_message'
                ),
                confirm: () => {
                    employeeApi
                        .deleteAbsence(id)
                        .then(() => {
                            this.$delete(this.absences, index);
                            showInfoMessage(
                                this.trans(
                                    'pages.employee_absence.absence_deleted'
                                )
                            );
                        })
                        .catch(() => {
                            showErrorMessage(
                                this.trans(
                                    'pages.employee_absence.absence_not_deleted'
                                )
                            );
                        });
                },
                closeOnCancel: true,
                closeOnOk: true,
                confirmButton: this.trans('pages.employee_absence.yes'),
                cancelButton: this.trans('pages.employee_absence.no'),
            });
        },
        edit(absence) {
            this.editAbsenceData = { ...absence };
            this.editing = true;
        },
        addAbsence() {
            if (
                this.$refs.addDate.valueState == 'Error' ||
                this.$refs.addDateEnd.valueState == 'Error'
            ) {
                return showErrorMessage(
                    this.trans('pages.employee_absence.invalid_date')
                );
            }
            if (
                !this.$refs.addDate.value ||
                this.$refs.addDate.value.length == 0
            ) {
                return showErrorMessage(
                    this.trans('pages.employee_absence.fill_required_fields')
                );
            }
            const addData = this.addAbsenceData;
            employeeApi
                .addAbsence(
                    addData.date,
                    addData.dateEnd,
                    addData.reason,
                    this.idParam
                )
                .then((response) => {
                    const absences = response.data.data;
                    if (
                        absences.some(
                            (item) =>
                                item.date.substring(0, 4) == this.selectedYear
                        )
                    ) {
                        this.getAbsences();
                    }
                    showInfoMessage(
                        this.trans('pages.employee_absence.absences_saved')
                    );
                })
                .catch(() => {
                    showErrorMessage(
                        this.trans('pages.employee_absence.absences_not_saved')
                    );
                });
        },
        updateAbsence() {
            if (this.$refs.editDate.valueState == 'Error') {
                return showErrorMessage(
                    this.trans('pages.employee_absence.invalid_date')
                );
            }
            if (
                !this.$refs.editDate.value ||
                this.$refs.addDate.value.length == 0
            ) {
                return showErrorMessage(
                    this.trans('pages.employee_absence.fill_required_fields')
                );
            }

            const id = this.editAbsenceData.id;
            const date = this.editAbsenceData.day;
            const reason = this.editAbsenceData.reason;

            employeeApi
                .updateAbsence(id, date, reason, this.idParam)
                .then(() => {
                    this.editing = false;
                    this.highlightedId = id;
                    window.setTimeout(() => {
                        this.highlightedId = null;
                    }, 500);
                    this.getAbsences();
                    showInfoMessage(
                        this.trans('pages.employee_absence.absence_modified')
                    );
                })
                .catch(() => {
                    showErrorMessage(
                        this.trans(
                            'pages.employee_absence.absence_not_modified'
                        )
                    );
                });
        },
        addForgottenDay() {
            if (this.$refs.forgottenDate.valueState == 'Error') {
                return showErrorMessage(
                    this.trans('pages.employee_absence.invalid_date')
                );
            }
            if (
                !this.$refs.forgottenDate.value ||
                this.$refs.forgottenDate.value.length == 0
            ) {
                return showErrorMessage(
                    this.trans('pages.employee_absence.fill_required_fields')
                );
            }
            this.addingForgotten = true;
            employeeApi
                .addForgottenDay(this.forgottenDate, this.idParam)
                .then(() => {
                    showInfoMessage(
                        this.trans('pages.employee_absence.forgotten_day_added')
                    );
                    this.$router.push('/employeeworkhours');
                })
                .catch(() => {
                    showErrorMessage(
                        this.trans(
                            'pages.employee_absence.forgotten_day_not_added'
                        )
                    );
                })
                .finally(() => {
                    this.addingForgotten = false;
                });
        },
    },
};
</script>
